import equal from 'deep-equal'
import React, { Fragment, useContext, useEffect, useRef, useState } from 'react'
import { HTTPClient, Session } from '../http'
import { PageContext } from '../site'
import { Button, Header, Icon, List, Menu, MenuIcon, MenuItem, Text, Tree, TreeItem, TreeNode } from '../html'
import { Content } from './ContentType'
import { ContentEditor, TabbedContentEditor, TreededContentEditor } from './ContentEditor'
import { CreateContentDialog } from './CreateContentDialog'
import { SiteContext } from '../site/siteContext'
import "./CollectionPage.css"

/**
 * Collection Page
 */
export const CollectionPage = (props) => {

    const site = useContext(SiteContext)
    const { id = "collection", icon, title, name = "Collection 1", schema, url, host, groupBy, editor, field, autosave = false, delay = 1000, actions, levels, onSelect, children } = props
    const [pageContext, setPageContext] = useContext(PageContext)
    const context = CollectionContext(url, field, host)

    useEffect(async () => {
        await context.load()
        setPageContext(context)
    }, [])

    async function reload() {
        await pageContext.load()
        setPageContext(Object.assign({}, pageContext))
    }

    function add() {
        const onOK = async (form) => {
            await pageContext.create(form);
            setPageContext(Object.assign({}, pageContext))
        }
        site.openDialog(<CreateContentDialog label={`${name}`} type={schema} onOK={onOK} />);
    }

    function renderActions() {
        return actions.map(element => {
            const action = () => element.props.action(pageContext)
            const clone = React.cloneElement(element, { action })
            return clone
        })
    }
    
    return (
        <Fragment>
            <Header className="collection-page" title={<Text>{title}</Text>}>
                <Button icon="add" label="Add" action={add} />
                <Button icon="refresh" label="Reload" action={reload} />
                {renderActions()}
            </Header>
            <menu className="collection-page">
                <Header title={<Text>Lista de {name}</Text>} >
                </Header>
                {levels ? <CollectionTree levels={levels} onSelect={onSelect}/> : <CollectionList groupBy={groupBy} onSelect={onSelect}/>}
            </menu>
            <main key={id} className="collection-page">
                <CollectionEditor icon={icon} schema={schema} layout={editor} autosave={autosave} delay={delay} />
                {children ? <article>{children}</article> : null}
            </main>
        </Fragment>
    )
}

/**
 * Collection List
 */
const CollectionList = (props) => {

    const { groupBy, onSelect } = props
    const [pageContext, setPageContext] = useContext(PageContext)
    const { all = [] } = pageContext

    function clear() {
        pageContext.clear()
        setPageContext(Object.assign({}, pageContext))
    }

    async function select(id) {
        clear()
        await pageContext.select(id)
        setPageContext(Object.assign({}, pageContext))
        if (onSelect) onSelect(id)
    }

    const items = all ? all.map(content => ({
        id: content.id,
        line1: content.name,
        line2: content.description,
        content
    })) : []

    return (
        <List items={items} onSelect={select} groupBy={groupBy} />
    )
}

/**
 * Collection Tree
 */
export const CollectionTree = (props) => {

    const { levels, onSelect } = props
    const [pageContext, setPageContext] = useContext(PageContext)
    const { all = [] } = pageContext

    function clear() {
        pageContext.clear()
        setPageContext(Object.assign({}, pageContext))
    }

    async function select(id) {
        clear()
        await pageContext.select(id)
        setPageContext(Object.assign({}, pageContext))
        if (onSelect) onSelect(id)
    }

    function group(items, by) {
        return items.reduce((nodes, field) => {
            let node = nodes.find(n => n.name === field[by])
            if (!node) {
                let name = field[by]
                node = { name, items: [], field: by }
                nodes.push(node)
            }
            node.items.push(field)
            return nodes
        }, [])
    }

    function generateNodes(levels, items) {
        const by = levels[0]
        const nodes = group(items, by)
        if (levels.length > 1) nodes.forEach(node => node.nodes = generateNodes(levels.slice(1), node.items))
        return nodes
    }

    function renderNodes(nodes) {
        return nodes.map(node => {
            const title = typeof node.name === 'boolean' ? `${node.field} = ${node.name}` : node.name
            return (
                <TreeNode key={node.name} icon={null} label={title} open={true}>
                    {node.nodes ? renderNodes(node.nodes) : node.items.map(item => <TreeItem key={item.id} id={item.id} label={item.name} onSelect={select}>item</TreeItem>)}
                </TreeNode>
            )
        })
    }

    const nodes = generateNodes(levels, all)
    return (
        <main>
            <Tree>
                {renderNodes(nodes)}
            </Tree>
        </main>
    )
}

/**
 * Collection Editor
 */
const CollectionEditor = (props) => {
    const [pageContext, setPageContext] = useContext(PageContext)
    const { selected } = pageContext
    const { icon, schema, layout, autosave = false, delay = 1000 } = props
    const timer = useRef(null)
    const [form, setForm] = useState(selected)

    useEffect(() => {
        setForm(selected)
    }, [selected])

    useEffect(() => {
        if (autosave) {
            clearTimeout(timer.current)
            timer.current = setTimeout(() => {
                if (canSave()) save()
            }, delay)
        }
    }, [form])

    function clear() {
        pageContext.clear()
        setPageContext(Object.assign({}, pageContext))
    }

    async function remove() {
        await pageContext.remove(selected.id)
        pageContext.clear()
        setPageContext(Object.assign({}, pageContext))
    }

    function change(next) {
        setForm(next)
    }

    function canSave() {
        const can = !equal(form, selected)
        return can
    }

    async function save() {
        console.log('saving.....')
        await pageContext.update(form)
        setPageContext(Object.assign({}, pageContext))
    }

    function renderEditor() {
        const content = new Content(schema, form)
        switch (layout) {
            case 'TABBED': return <TabbedContentEditor {...props} content={content} onChange={change} />
            case 'TREEDED': return <TreededContentEditor {...props} content={content} onChange={change} />
            default: return <ContentEditor {...props} content={content} onChange={change} />
        }
    }

    return selected && form ? (
        <Fragment>
            <Header icon={icon} title={<Text>{selected.name}</Text>}>
                <Icon icon="close" clickable action={clear} />
                <MenuIcon align="alignRight">
                    <Menu>
                        <MenuItem label="Eliminar" onSelect={remove} />
                    </Menu>
                </MenuIcon>
                {autosave === true ? null : <Button icon="save" label="Guardar Cambios" raised disabled={!canSave()} action={save} />}
            </Header>
            {renderEditor()}
        </Fragment>
    ) : null
}

/**
 * Collection Context
 */
export const CollectionContext = (url, field, host) => {

    const API = CollectionAPI(url, host)

    return {

        all: [],
        selected: null,

        async load() {
            try {
                const data = await API.all();
                this.all = field ? data[field] : data;
            } catch (error) {
                console.log(error)
            }
            return
        },

        select(id) {
            const result = this.all.find(item => item.id === id);
            this.selected = result;
        },

        clear() {
            this.selected = null
        },

        async create(form) {
            try {
                await API.create(form);
                await this.load();
            } catch (error) {
                console.log("CREATE ERROR", error)
            }
            return
        },

        async update(form) {
            try {
                await API.update(form)
                await this.load()
            } catch (error) {
                console.log(error)
            }
            return
        },

        async patch(id, form) {
            try {
                await API.patch(id, form)
                await this.load()
            } catch (error) {
                console.log(error)
            }
            return
        },

        async updateProperty(id, propertyName, form) {
            try {
                await API.updateProperty(id, propertyName, form)
                await this.load()
            } catch (error) {
                console.log(error)
            }
            return
        },

        async remove(id) {
            try {
                await API.remove(id)
                await this.load()
            } catch (error) {
                console.log(error)
            }
            return
        },
    }
}

/**
 * Collection API
 */
const CollectionAPI = (url, host) => {

    const http = HTTPClient(host || window.API || process.env.REACT_APP_API, Session);

    return {
        all(filters) {
            let queryParams = "?"
            if (filters) {
                const filterQuery = Object.keys(filters).reduce((query, key) => {
                    const value = filters[key]
                    if (typeof (value) === 'boolean') {
                        return query.concat(`${key}=${value}&`)
                    } else if (Array.isArray(value)) {
                        const param = value.length === 0 ? '' : value.reduce((param, item) => {
                            param = param.concat(`${key}=${item}&`)
                            return param
                        }, "")
                        return query.concat(param)
                    } else {
                        return query.concat(`${key}=%${filters[key]}%&`)
                    }
                }, "")
                queryParams = queryParams.concat(filterQuery)
            }
            return http.GET(url)
        },

        find(id) {
            return http.GET(`${url}/${id}`)
        },

        create(form) {
            const body = JSON.stringify(form)
            return http.POST(url, body)
        },

        update(form) {
            const body = JSON.stringify(form)
            return http.PUT(`${url}/${form.id}`, body)
        },

        patch(id, form) {
            const body = JSON.stringify(form)
            return http.PATCH(`${url}/${id}`, body)
        },

        updateProperty(id, propertyName, form) {
            const body = JSON.stringify(form)
            return http.PUT(`${url}/${id}/${propertyName}`, body)
        },

        remove(id) {
            return http.DELETE(`${url}/${id}`)
        }

    }
}